import axios from 'axios';
import storeJs from 'store';
import { logout } from 'state/session/actions';
import { STORE_KEY_REMEMBER_TOKEN } from 'state/session/constants';
import browserHistory from 'state/browserHistory';
import createSagaMiddleware from 'redux-saga';
import { applyMiddleware, compose, createStore } from 'redux';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import React from 'react';
import App from '../App';
import rootSaga from '../state/saga';
import reducer from '../state';

// Hosts that belong to our own backend and are therefore allowed to receive the
// session token. `mokapos.com` is kept for backward compatibility; the
// configured backend URLs cover non-prod hosts (e.g. localhost during testing).
const OWN_BACKEND_HOSTS = [process.env.REACT_APP_SERVICE_BACKEND_URL].filter(
  Boolean
);

function isOwnBackendRequest(url = '') {
  return (
    url.includes('mokapos.com') ||
    OWN_BACKEND_HOSTS.some(host => url.includes(host))
  );
}

function configureRequestInterceptor(config) {
  if (isOwnBackendRequest(config.url) && !config.headers.Authorization) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = storeJs.get(STORE_KEY_REMEMBER_TOKEN);
  }
  return config;
}

function reInitializeStore() {
  const sagaMiddleware = createSagaMiddleware();
  const composeEnhancers =
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
  const store = createStore(
    reducer,
    composeEnhancers(applyMiddleware(sagaMiddleware))
  );
  sagaMiddleware.run(rootSaga);

  return store;
}

function configureResponseErrorInterceptor(error) {
  if (error.response) {
    if (error.response.status === 401) {
      const store = reInitializeStore();
      store.dispatch(logout());
      browserHistory.push('/login');

      ReactDOM.render(
        <Provider store={store}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </Provider>,
        document.getElementById('root')
      );

      return Promise.reject(new Error('You must be signed in'));
    }

    if (error.response.status === 403) {
      return Promise.reject(
        new Error('You are not authorized to perform this action')
      );
    }
  }
  return Promise.reject(error);
}

export default function() {
  axios.interceptors.request.use(configureRequestInterceptor, error =>
    Promise.reject(error)
  );

  axios.interceptors.response.use(
    config => config,
    error => configureResponseErrorInterceptor(error)
  );
}
